import React, { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from "recharts";
import { CheckCircle2, Clock, Wallet } from "lucide-react";
import EmployeeShell from "../../components/layout/EmployeeShell";
import RefundRuleCard from "../../components/RefundRuleCard";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { DashboardApi } from "../../lib/api";
import { formatMoney, monthLabel } from "../../lib/format";
import { Skeleton } from "../../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const EmployeeRefunds = () => {
  const { t } = useI18n();
  const { currency } = useAuth();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    DashboardApi.mySummary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, []);

  if (!summary) {
    return (
      <EmployeeShell title={t("employee.refundsTitle")}>
        <div className="space-y-3">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      </EmployeeShell>
    );
  }

  const totals = summary.totals || {};
  const chartData = [...(summary.by_month || [])].reverse().map((m) => ({
    ...m,
    name: (m.month || "").slice(5),
  }));

  return (
    <EmployeeShell title={t("employee.refundsTitle")} subtitle={t("employee.refundsSub")}>
      <div className="ll-card p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
          <Wallet className="h-3.5 w-3.5" /> {t("employee.totalEarned")}
        </div>
        <div
          className="ll-num mt-1 font-display text-3xl font-semibold text-primary"
          data-testid="employee-refund-total"
        >
          {formatMoney(totals.refund, currency)}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-2.5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5 text-primary" /> {t("receipts.payoutPaid")}
            </div>
            <div
              className="ll-num mt-0.5 font-semibold text-primary"
              data-testid="employee-refund-paid"
            >
              {formatMoney(totals.refund_paid, currency)}
            </div>
          </div>
          <div className="rounded-lg border p-2.5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> {t("receipts.payoutUnpaid")}
            </div>
            <div className="ll-num mt-0.5 font-semibold" data-testid="employee-refund-outstanding">
              {formatMoney(
                totals.refund_outstanding ?? (totals.refund || 0) - (totals.refund_paid || 0),
                currency
              )}
            </div>
          </div>
        </div>
      </div>

      {summary.my_rule ? (
        <div className="mt-4">
          <RefundRuleCard rule={summary.my_rule} currency={currency} />
        </div>
      ) : null}

      <div className="ll-card mt-4 p-4">
        <h2 className="font-display text-sm font-semibold">{t("employee.byMonth")}</h2>
        <div className="mt-3 h-[180px]">
          {chartData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} width={46} />
                <RTooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid hsl(var(--border))",
                    background: "hsl(var(--card))",
                    fontSize: 12,
                  }}
                  formatter={(v, n) => [formatMoney(v, currency), n]}
                />
                <Bar dataKey="refund" name={t("common.refund")} fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {t("employee.noRefunds")}
            </div>
          )}
        </div>
      </div>

      <div className="ll-card mt-4 overflow-hidden">
        <div className="border-b px-4 py-3">
          <h2 className="font-display text-sm font-semibold">{t("employee.monthlyDetail")}</h2>
        </div>
        {summary.by_month?.length ? (
          <Table data-testid="employee-month-table">
            <TableHeader className="bg-muted/40">
              <TableRow>
                <TableHead>{t("employee.byMonth")}</TableHead>
                <TableHead className="text-right">{t("common.receipts")}</TableHead>
                <TableHead className="text-right">{t("employee.spent")}</TableHead>
                <TableHead className="text-right">{t("common.refund")}</TableHead>
                <TableHead className="text-right">{t("receipts.payoutPaid")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.by_month.map((m) => (
                <TableRow key={m.month}>
                  <TableCell className="text-sm font-medium">{monthLabel(m.month)}</TableCell>
                  <TableCell className="ll-num text-right">{m.count}</TableCell>
                  <TableCell className="ll-num text-right">{formatMoney(m.gross, currency)}</TableCell>
                  <TableCell className="ll-num text-right">
                    {formatMoney(m.refund, currency)}
                  </TableCell>
                  <TableCell className="ll-num text-right text-primary">
                    {formatMoney(m.paid, currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="p-4 text-sm text-muted-foreground">{t("employee.startScanning")}</p>
        )}
      </div>
    </EmployeeShell>
  );
};

export default EmployeeRefunds;
